<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Upload Button Component
      p.text--secondary A button component that handles file uploads with image processing capabilities.

    // Upload Button Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in uploadVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            yz-upload-button(v-if="variation.id === 'basic-upload'" v-bind="variation.props" v-model="basicUploadFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'multiple-files'" v-bind="variation.props" v-model="multipleFilesFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'custom-accept'" v-bind="variation.props" v-model="customAcceptFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'styled-upload'" v-bind="variation.props" v-model="styledUploadFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'append-files'" v-bind="variation.props" v-model="appendFilesFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'immediate-upload'" v-bind="variation.props" v-model="immediateUploadFiles" @upload-progress="handleUploadProgress" @error="handleUploadError" @upload-success="handleUploadSuccess" @upload-error="handleUploadError")
            yz-upload-button(v-else-if="variation.id === 'file-size-limit'" v-bind="variation.props" v-model="fileSizeLimitFiles" @error="handleUploadError")
            
            template(v-if="variationFiles[variation.id] && variationFiles[variation.id].length > 0")
              .mt-4(style="max-width: 500px;")
                h5.mb-2 Uploaded Files:
                yz-file-preview(v-model="variationFiles[variation.id]")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}
              
            template(v-if="variationFiles[variation.id] && variationFiles[variation.id].length > 0")
              .mt-3
                .code-header.d-flex.align-center.mb-2
                  span.font-weight-medium Current v-model Data:
                pre.code-block.pa-3.rounded.text-caption(style="background-color: #e8f5e8; overflow-x: auto;")
                  code {{ formatModelData(variationFiles[variation.id]) }}

    // File Upload Examples
    .component-section.mb-8#file-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('file-examples')") File Upload Examples
      .variations-grid
        .variation-item.mb-6(v-for="example in fileExamples" :key="example.name" :id="example.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(example.id)") {{ example.name }}
            p.text--secondary.caption {{ example.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            yz-upload-button(v-if="example.id === 'profile-upload'" v-bind="example.props" v-model="profileUploadFiles" @error="handleUploadError")
            yz-upload-button(v-else-if="example.id === 'gallery-upload'" v-bind="example.props" v-model="galleryUploadFiles" @error="handleUploadError")
            
            template(v-if="exampleFiles[example.id] && exampleFiles[example.id].length > 0")
              .mt-4(style="max-width: 500px;")
                h5.mb-2 Selected Files:
                yz-file-preview(v-model="exampleFiles[example.id]" :show-actions="false")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === example.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === example.code ? 'success' : ''" @click="copyCode(example.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ example.code }}
              
            template(v-if="exampleFiles[example.id] && exampleFiles[example.id].length > 0")
              .mt-3
                .code-header.d-flex.align-center.mb-2
                  span.font-weight-medium Current v-model Data:
                pre.code-block.pa-3.rounded.text-caption(style="background-color: #e8f5e8; overflow-x: auto;")
                  code {{ formatModelData(exampleFiles[example.id]) }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in uploadButtonProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

    // Events Documentation
    .component-section.mb-8#events
      h3.mb-4.cursor-pointer(@click="navigateToSection('events')") Events
      .events-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Payload
                th Description
            tbody
              tr(v-for="event in uploadButtonEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.payload }}
                td.single-line {{ event.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      variationFiles: {
        'basic-upload': [],
        'multiple-files': [],
        'custom-accept': [],
        'styled-upload': [],
        'append-files': [],
        'immediate-upload': [],
        'file-size-limit': []
      },
      exampleFiles: {
        'profile-upload': [],
        'gallery-upload': []
      },
      uploadVariations: [
        {
          name: 'Basic Upload',
          id: 'basic-upload',
          description: 'A simple upload button with default settings',
          props: {
            label: 'Upload Image'
          },
          code: 'yz-upload-button(label="Upload Image" v-model="files")'
        },
        {
          name: 'Multiple Files',
          id: 'multiple-files',
          description: 'Allow selecting multiple files at once',
          props: {
            label: 'Upload Images',
            multiple: true
          },
          code: 'yz-upload-button(label="Upload Images" multiple v-model="files")'
        },
        {
          name: 'Custom Accept Types',
          id: 'custom-accept',
          description: 'Restrict file types that can be uploaded',
          props: {
            label: 'Upload Documents',
            accept: '.pdf,.doc,.docx'
          },
          code: 'yz-upload-button(label="Upload Documents" accept=".pdf,.doc,.docx" v-model="files")'
        },
        {
          name: 'Styled Upload Button',
          id: 'styled-upload',
          description: 'Upload button with custom styling',
          props: {
            label: 'Choose Files',
            outlined: true,
            prependIcon: 'mdi-cloud-upload'
          },
          code: 'yz-upload-button(label="Choose Files" outlined prepend-icon="mdi-cloud-upload" v-model="files")'
        },
        {
          name: 'Append Files Mode',
          id: 'append-files',
          description: 'Multiple file upload that appends new files to existing ones',
          props: {
            label: 'Add More Files',
            multiple: true,
            appendFiles: true,
            secondary: true,
            prependIcon: 'mdi-plus'
          },
          code: 'yz-upload-button(label="Add More Files" multiple append-files secondary prepend-icon="mdi-plus" v-model="files")'
        },
        {
          name: 'Immediate Upload Mode',
          id: 'immediate-upload',
          description: 'Upload files immediately to API endpoint with real-time progress tracking',
          props: {
            label: 'Upload to Server',
            uploadMode: 'immediate',
            uploadUrl: '/documents/',
            multiple: true,
            appendFiles: true,
            color: 'primary',
            prependIcon: 'mdi-cloud-upload'
          },
          code: `yz-upload-button(
  label="Upload to Server"
  upload-mode="immediate"
  upload-url="/documents/"
  multiple
  append-files
  color="primary"
  prepend-icon="mdi-cloud-upload"
  @upload-success="handleUploadSuccess"
  @upload-error="handleUploadError"
  v-model="files"
)`
        },
        {
          name: 'File Size Limit',
          id: 'file-size-limit',
          description: 'Upload button with custom file size limit validation',
          props: {
            label: 'Upload (Max 2MB)',
            multiple: true,
            fileSizeLimit: 2,
            color: 'warning',
            prependIcon: 'mdi-file-check'
          },
          code: `yz-upload-button(
  label="Upload (Max 2MB)"
  multiple
  :file-size-limit="2"
  color="warning"
  prepend-icon="mdi-file-check"
  v-model="files"
)`
        }
      ],
      fileExamples: [
        {
          name: 'Profile Picture Upload',
          id: 'profile-upload',
          description: 'Single image upload for profile pictures',
          props: {
            label: 'Upload Profile Picture',
            accept: 'image/*',
            multiple: false,
            color: 'primary',
            prependIcon: 'mdi-account-circle'
          },
          code: `yz-upload-button(
  label="Upload Profile Picture"
  accept="image/*"
  :multiple="false"
  color="primary"
  prepend-icon="mdi-account-circle"
  v-model="profilePicture"
)`
        },
        {
          name: 'Gallery Upload',
          id: 'gallery-upload',
          description: 'Multiple image upload for galleries',
          props: {
            label: 'Add to Gallery',
            accept: 'image/*',
            multiple: true,
            color: 'success',
            prependIcon: 'mdi-image-multiple'
          },
          code: `yz-upload-button(
  label="Add to Gallery"
  accept="image/*"
  multiple
  color="success"
  prepend-icon="mdi-image-multiple"
  v-model="galleryImages"
)`
        }
      ],
      uploadButtonProps: [
        {
          name: 'value',
          type: 'Array',
          default: '[]',
          description: 'Array of uploaded files with blob and file properties (v-model)'
        },
        {
          name: 'multiple',
          type: 'Boolean',
          default: 'false',
          description: 'Allow multiple file selection'
        },
        {
          name: 'accept',
          type: 'String',
          default: 'image/*',
          description: 'File types accepted by the file input'
        },
        {
          name: 'appendFiles',
          type: 'Boolean',
          default: 'false',
          description: 'When true and multiple is true, append new files to existing v-model instead of replacing them'
        },
        {
          name: 'label',
          type: 'String',
          default: 'Upload',
          description: 'Button label text'
        },
        {
          name: 'size',
          type: 'String',
          default: 'md',
          description: 'Button size (xs, sm, md, lg, xl)'
        },
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Button color theme'
        },
        {
          name: 'loading',
          type: 'Boolean',
          default: 'false',
          description: 'Show loading state'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the upload button'
        },
        {
          name: 'outlined',
          type: 'Boolean',
          default: 'false',
          description: 'Use outlined button style'
        },
        {
          name: 'prependIcon',
          type: 'String',
          default: 'null',
          description: 'Icon to show before the label'
        },
        {
          name: 'appendIcon',
          type: 'String',
          default: 'null',
          description: 'Icon to show after the label'
        },
        {
          name: 'uploadMode',
          type: 'String',
          default: 'preview',
          description: 'Upload strategy: "preview" (create blob URLs) or "immediate" (upload to API immediately)'
        },
        {
          name: 'uploadUrl',
          type: 'String',
          default: '/documents/',
          description: 'API endpoint for immediate upload mode'
        },
        {
          name: 'uploadHeaders',
          type: 'Object',
          default: '{}',
          description: 'Custom headers for upload requests in immediate mode'
        },
        {
          name: 'uploadMethod',
          type: 'String',
          default: 'POST',
          description: 'HTTP method for upload requests in immediate mode'
        },
        {
          name: 'fileSizeLimit',
          type: 'Number',
          default: '5',
          description: 'Maximum file size limit in MB. Files exceeding this limit will be rejected with an error message.'
        }
      ],
      uploadButtonEvents: [
        {
          name: 'input',
          payload: 'Array',
          description: 'Emitted when files are uploaded for v-model binding, contains array of {blob, file} objects'
        },
        {
          name: 'error',
          payload: 'Error',
          description: 'Emitted when an error occurs during file processing'
        },
        {
          name: 'upload-start',
          payload: '{file, fileId}',
          description: 'Emitted when upload begins in immediate mode'
        },
        {
          name: 'upload-progress',
          payload: '{file, fileId, progress}',
          description: 'Emitted during upload with progress percentage in immediate mode'
        },
        {
          name: 'upload-success',
          payload: '{file, fileId, response}',
          description: 'Emitted when upload completes successfully in immediate mode'
        },
        {
          name: 'upload-error',
          payload: '{file, fileId, error}',
          description: 'Emitted when upload fails in immediate mode'
        }
      ]
    }
  },
  computed: {
    basicUploadFiles: {
      get() { return this.variationFiles['basic-upload'] },
      set(value) { this.$set(this.variationFiles, 'basic-upload', value) }
    },
    multipleFilesFiles: {
      get() { return this.variationFiles['multiple-files'] },
      set(value) { this.$set(this.variationFiles, 'multiple-files', value) }
    },
    customAcceptFiles: {
      get() { return this.variationFiles['custom-accept'] },
      set(value) { this.$set(this.variationFiles, 'custom-accept', value) }
    },
    styledUploadFiles: {
      get() { return this.variationFiles['styled-upload'] },
      set(value) { this.$set(this.variationFiles, 'styled-upload', value) }
    },
    profileUploadFiles: {
      get() { return this.exampleFiles['profile-upload'] },
      set(value) { this.$set(this.exampleFiles, 'profile-upload', value) }
    },
    galleryUploadFiles: {
      get() { return this.exampleFiles['gallery-upload'] },
      set(value) { this.$set(this.exampleFiles, 'gallery-upload', value) }
    },
    appendFilesFiles: {
      get() { return this.variationFiles['append-files'] },
      set(value) { this.$set(this.variationFiles, 'append-files', value) }
    },
    immediateUploadFiles: {
      get() { return this.variationFiles['immediate-upload'] },
      set(value) { this.$set(this.variationFiles, 'immediate-upload', value) }
    },
    fileSizeLimitFiles: {
      get() { return this.variationFiles['file-size-limit'] },
      set(value) { this.$set(this.variationFiles, 'file-size-limit', value) }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      })
    },
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    handleUploadError(error) {
      console.error('Upload error:', error)
    },
    handleUploadSuccess(data) {
      console.log('Upload success:', data)
    },
    handleUploadProgress(data) {
      console.log('Upload progress:', data)
},
    formatModelData(files) {
      if (!files || !files.length) return '[]'

      const truncatedFiles = files.map(file => ({
        ...file,
        blob: file.blob ? file.blob.substring(0, 50) + '...' : null,
        file: file.file ? "<file_object>" : undefined,
        uploadResponse: file.uploadResponse ? "<upload_response_object>" : undefined
      }))

      return JSON.stringify(truncatedFiles, null, 2)
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table, .events-table {
  overflow-x: auto;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

</style>